"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";
import { parseSlug, buildSlug } from "./parseSlug";

/**
 * Lokalizacja bieżącego katalogu wyciągnięta z URL-a (client-side).
 *
 * Używane przez `CatalogLoadMore` (paginacja "Wczytaj więcej") do:
 *   - zbudowania URL-a kolejnej strony (`buildPageHref`) - format ILINK `pageN`
 *     (symetryczny z backendem, zob. `parseSlug`/`buildSlug`),
 *   - parametrów AJAX-a doczytującego treść (`slug` + `locale` + `articleId`).
 */
export type CatalogLocation = {
  /** Locale z pierwszego segmentu ścieżki (`/pl/...` -> `pl`). */
  locale: string;
  /** Path katalogu (segmenty bez locale i bez extras), slash-joined. */
  slug: string;
  /** Path katalogu jako tablica segmentów. */
  pathSegments: string[];
  /** ID artykułu/galerii z URL extras (`symbol-,123`) lub null. */
  articleId: number | null;
  /** Bieżąca strona z URL (`pageN`) lub 1 gdy brak. */
  page: number;
  /**
   * Buduje pełny href dla danej strony (z locale prefix). Strona 1 = URL bazowy
   * (bez `pageN`). Zachowuje akcje i artykuł z bieżącego URL.
   */
  buildPageHref: (page: number) => string;
};

/**
 * Hook: parsuje bieżący `usePathname()` do struktury katalogu + buildera URL stron.
 *
 * @returns {@see CatalogLocation}
 */
export function useCatalogLocation(): CatalogLocation {
  const pathname = usePathname() || "/";

  return useMemo(() => {
    /* Trailing slash = czysty katalog (NOWY STANDARD). Client widzi go wprost
       w `usePathname()` (przeglądarka zachowuje slash) - inaczej niż server,
       który musi go czytać z nagłówka `x-imp-trailing-slash` (proxy.ts). */
    const hasTrailingSlash = pathname.length > 1 && pathname.endsWith("/");
    const segs = pathname.split("/").filter(Boolean);
    const locale = segs[0] ?? "pl";
    const parsed = parseSlug(locale, segs.slice(1), hasTrailingSlash);
    const page = parsed.pages[0] ?? 1;

    const buildPageHref = (p: number): string => {
      const slugSegs = buildSlug({
        pathSegments: parsed.pathSegments,
        actions: parsed.actions,
        pages: p > 1 ? [p] : [],
        articles: parsed.articles,
      });
      return "/" + [locale, ...slugSegs].join("/");
    };

    return {
      locale,
      slug: parsed.pathSegments.join("/"),
      pathSegments: parsed.pathSegments,
      articleId: parsed.articles[0]?.id ?? null,
      page,
      buildPageHref,
    };
  }, [pathname]);
}
